'use client'

import { format } from 'date-fns'
import { Calendar as CalendarIcon, ChevronUp } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { cn } from '@/_lib/utils'
import { ITemplate, ITemplateTimeslot } from '@/_types'
import { SECTION_THEME_ENUM } from '@/_types/base'
import { useGetTimeSlots } from '@/app/[locale]/order/_services/useGetTimeSlots'
import { calculateOptimalCalendarWeek } from '@/app/[locale]/order/_utils/calendar.utils'
import { getOrderThemeClasses } from '@/app/[locale]/order/_utils/order-flow-section-theme-classes'

import { templateCalendarQuickSlotButtonClass } from './a-product-template-calendar-slot-button.class'
import { AProductTemplateCalendarView } from './a-product-template-calendar-view'

import type { CalendarTimeslot, DateSlot } from './a-product-template-calendar-types'

/** Re-exports {@link DateSlot} and {@link CalendarTimeslot} for consumers of this module. */
export type { DateSlot, CalendarTimeslot } from './a-product-template-calendar-types'

const slotButtonClass = cn(templateCalendarQuickSlotButtonClass, 'w-fit')

const calendarToggleButtonClass = cn(
	'inline-flex size-9 shrink-0 items-center justify-center rounded-lg p-0',
	'text-white',
	'bg-medante-pink-secondary hover:bg-medante-pink-secondary/80 transition-colors duration-200 cursor-pointer rounded-full'
)

/**
 * Wires template quick slots, expandable calendar, and `useGetTimeSlots` for the selected work week.
 *
 * @param template - Booking template whose `id` and `timeslots` drive queries and quick buttons.
 * @param onSelect - Optional callback when the user confirms a quick or calendar slot.
 * @returns Open state, current `{ from, to }` range, mapped `calendarSlots`, loading flag, and handlers.
 *
 * @remarks The time-slot query is only enabled when the calendar is open and the range is set.
 */
export function useProductTemplateCalendar(
	template: ITemplate,
	onSelect?: (template: ITemplate, timeslot: ITemplateTimeslot) => void
) {
	const [calendarOpen, setCalendarOpen] = useState(false)
	const [calendarRange, setCalendarRange] = useState<{ from: string; to: string } | null>(null)

	const { data: timeSlotsResponse, isPending: isCalendarSlotsPending } = useGetTimeSlots(
		template.id,
		calendarRange?.from ?? '',
		calendarRange?.to ?? '',
		{
			enabled:
				calendarOpen &&
				!!calendarRange?.from &&
				!!calendarRange?.to &&
				template.timeslots.length > 0
		}
	)

	const calendarSlots: DateSlot[] = useMemo(() => {
		if (!timeSlotsResponse?.data) return []
		return timeSlotsResponse.data.map(slot => {
			const startDate = new Date(slot.startTime)
			const endDate = new Date(slot.endTime)
			const durationMs = endDate.getTime() - startDate.getTime()
			const durationMinutes = Math.round(durationMs / (1000 * 60))
			return {
				id: slot.id,
				date: format(startDate, 'yyyy-MM-dd'),
				startTime: format(startDate, 'HH:mm'),
				duration: durationMinutes,
				startTimeISO: slot.startTime,
				endTimeISO: slot.endTime,
				free: slot.free
			}
		})
	}, [timeSlotsResponse?.data])

	const toggleCalendar = useCallback(() => {
		if (calendarOpen) {
			setCalendarOpen(false)
			return
		}
		const { dateFrom, dateTo } = calculateOptimalCalendarWeek([template], template.id)
		setCalendarRange({ from: dateFrom, to: dateTo })
		setCalendarOpen(true)
		requestAnimationFrame(() => {
			document
				.getElementById(`order-calendar-${template.id}`)
				?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
		})
	}, [calendarOpen, template])

	const handleWeekChange = useCallback((dateFrom: string, dateTo: string) => {
		setCalendarRange({ from: dateFrom, to: dateTo })
	}, [])

	const emitTimeslot = useCallback(
		(timeslot: ITemplateTimeslot) => {
			onSelect?.(template, timeslot)
		},
		[onSelect, template]
	)

	const handleCalendarSlotSelect = useCallback(
		(slot: CalendarTimeslot) => {
			if (!slot?.id) return
			emitTimeslot({
				id: slot.id,
				startTime: slot.startTime,
				endTime: slot.endTime,
				free: slot.free,
				template
			})
		},
		[emitTimeslot, template]
	)

	return {
		template,
		calendarOpen,
		calendarRange,
		calendarSlots,
		isCalendarSlotsPending,
		toggleCalendar,
		handleWeekChange,
		handleCalendarSlotSelect,
		emitTimeslot
	}
}

/**
 * Row of quick next-slot buttons plus a toggle that opens the full week calendar panel.
 *
 * @param props - Quick row props from {@link useProductTemplateCalendar}.
 * @param props.template - Template whose `timeslots` populate the pill buttons.
 * @param props.calendarOpen - Whether the expanded calendar is visible (drives icon and `aria-expanded`).
 * @param props.toggleCalendar - Opens the panel (sets optimal week + fetches) or closes it.
 * @param props.emitTimeslot - Invoked with a free quick `ITemplateTimeslot` on pill click.
 * @param props.className - Optional classes on the flex wrapper.
 */
export function AProductTemplateCalendarQuickRow({
	template,
	calendarOpen,
	toggleCalendar,
	emitTimeslot,
	className
}: {
	template: ITemplate
	calendarOpen: boolean
	toggleCalendar: () => void
	emitTimeslot: (timeslot: ITemplateTimeslot) => void
	className?: string
}) {
	return (
		<div className={cn('flex flex-start flex-wrap gap-1 lg:gap-2', className)}>
			{template.timeslots.map(timeslot => (
				<button
					key={timeslot.id}
					type='button'
					disabled={!timeslot.free}
					onClick={() => emitTimeslot(timeslot)}
					className={slotButtonClass}
				>
					{format(new Date(timeslot.startTime), 'dd.MM.yyyy')}
					<div className='h-0.5 w-0.5 shrink-0 rounded-full bg-white' aria-hidden />
					{format(new Date(timeslot.startTime), 'HH:mm')}
				</button>
			))}

			<button
				type='button'
				className={calendarToggleButtonClass}
				aria-expanded={calendarOpen}
				aria-label={calendarOpen ? 'Skryť kalendár termínov' : 'Zobraziť kalendár termínov'}
				onClick={toggleCalendar}
			>
				{calendarOpen ? (
					<ChevronUp className='size-4 shrink-0' strokeWidth={2} aria-hidden />
				) : (
					<CalendarIcon className='size-4 shrink-0' strokeWidth={2} aria-hidden />
				)}
			</button>
		</div>
	)
}

/**
 * Scroll anchor + shell around {@link AProductTemplateCalendarView} for the order flow.
 *
 * @param props - Expanded panel props.
 * @param props.templateId - Used for stable `id={`order-calendar-${templateId}`}` scrolling.
 * @param props.calendarRange - Inclusive-ish week window as `from` / `to` `yyyy-MM-dd` strings.
 * @param props.calendarSlots - Slots mapped to {@link DateSlot} for the grid views.
 * @param props.isSlotsLoading - Shows loading UI inside the view while refetching.
 * @param props.onSlotSelect - Receives a {@link CalendarTimeslot} when the user picks a slot.
 * @param props.onWeekChange - Forwards week navigation to the parent to refetch slots.
 * @param props.className - Optional classes merged onto the outer shell.
 */
export function AProductTemplateCalendarExpandedPanel({
	templateId,
	calendarRange,
	calendarSlots,
	isSlotsLoading,
	onSlotSelect,
	onWeekChange,
	sectionTheme = null,
	className
}: {
	templateId: number
	calendarRange: { from: string; to: string }
	calendarSlots: DateSlot[]
	isSlotsLoading?: boolean
	onSlotSelect: (slot: CalendarTimeslot) => void
	onWeekChange: (dateFrom: string, dateTo: string) => void
	sectionTheme?: SECTION_THEME_ENUM | null
	className?: string
}) {
	return (
		<div
			id={`order-calendar-${templateId}`}
			className={cn(
				'overflow-hidden rounded-xl',
				getOrderThemeClasses(sectionTheme ?? null).calendarExpandedShell,
				className
			)}
		>
			<AProductTemplateCalendarView
				slots={calendarSlots}
				isSlotsLoading={isSlotsLoading}
				onSlotSelect={onSlotSelect}
				dateFrom={calendarRange.from}
				dateTo={calendarRange.to}
				onWeekChange={onWeekChange}
			/>
		</div>
	)
}

/**
 * Props for the standalone {@link AProductTemplateCalendar} stack component.
 *
 * @property template - Booking template shown in quick row + calendar.
 * @property onSelect - Optional selection handler for both quick and expanded slots.
 * @property className - Optional classes on the outer vertical flex wrapper.
 */
export interface AProductTemplateCalendarProps {
	template: ITemplate
	onSelect?: (template: ITemplate, timeslot: ITemplateTimeslot) => void
	className?: string
}

/**
 * Vertical stack: quick slot row plus expanded calendar (mobile-friendly standalone layout).
 *
 * @param props - Same contract as {@link AProductTemplateCalendarProps}.
 */
function AProductTemplateCalendar({
	template,
	onSelect,
	className
}: AProductTemplateCalendarProps) {
	const cal = useProductTemplateCalendar(template, onSelect)

	return (
		<div className={cn('flex flex-col gap-3', className)}>
			<AProductTemplateCalendarQuickRow
				template={cal.template}
				calendarOpen={cal.calendarOpen}
				toggleCalendar={cal.toggleCalendar}
				emitTimeslot={cal.emitTimeslot}
			/>
			{cal.calendarOpen && cal.calendarRange ? (
				<AProductTemplateCalendarExpandedPanel
					templateId={cal.template.id}
					calendarRange={cal.calendarRange}
					calendarSlots={cal.calendarSlots}
					isSlotsLoading={cal.isCalendarSlotsPending}
					onSlotSelect={cal.handleCalendarSlotSelect}
					onWeekChange={cal.handleWeekChange}
				/>
			) : null}
		</div>
	)
}

export default AProductTemplateCalendar
